import { MotifIcon } from "@ey-xd/motif-react";
import { useState } from "react";
import "./EmailNotification.scss";
import { useNavigate } from "react-router";
import {
  IconoirInfoCircle,
  IconoirWarningCircle,
  IconoirCheckCircle,
  IconoirXmark,
  IconoirArrowRight,
} from "@ey-xd/motif-icon";

type Variant = "error" | "success" | "info" | "warning";

type EmailNotificationProps = {
  show?: boolean;
  message?: string;
  type?: Variant;
  supportingText?: string | React.ReactNode;
  onCloseHandler?: () => void;
  subMessage?: string;
  actionRedirectUrl?: string;
  actionRedirectLabel?: string;
  hideCloseBtn?: boolean;
  theme?: "light" | "dark";
  scrollToSection?: string;
};

const variantIconMap: Record<Variant, typeof IconoirInfoCircle> = {
  info: IconoirInfoCircle,
  warning: IconoirWarningCircle,
  error: IconoirWarningCircle,
  success: IconoirCheckCircle,
};

export default function EmailNotification({
  show = true,
  message = "",
  type = "info",
  supportingText = "",
  onCloseHandler,
  actionRedirectUrl = "",
  actionRedirectLabel = "",
  hideCloseBtn = false,
  theme = "light",
  scrollToSection = "",
}: EmailNotificationProps) {
  const [showMessage, setShowMessage] = useState({
    show: show,
    type: type,
    message: message,
    supportingText: supportingText,
    actionRedirectUrl: actionRedirectUrl,
    actionRedirectLabel: actionRedirectLabel,
  });
  const closeMessageHandler = () => {
    if (onCloseHandler) {
      onCloseHandler();
    }
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };

  if (!showMessage.show) {
    return null;
  }
  const navigate = useNavigate();

  return (
    <div
      motif-theme={theme}
      className={`email-notification ${theme} ${hideCloseBtn ? "hide-close-btn" : ""}`}
    >
      <div className={`email-notification-grid email-notification--${type}`}>
        <div className="email-notification-icon">
          <MotifIcon iconFunction={variantIconMap[type]} size="20" />
        </div>
        <div className="email-notification-content">
          <div className="email-notification-message">{message}</div>
          {supportingText && (
            <div className="email-notification-supporting-text">
              {supportingText}
            </div>
          )}
          {actionRedirectUrl &&
            actionRedirectLabel &&
            scrollToSection === "" && (
              <button
                className="email-notification-action-button"
                onClick={() => navigate(actionRedirectUrl)}
                aria-label={actionRedirectLabel}
              >
                <span className="action-label">
                  {actionRedirectLabel}
                  <MotifIcon iconFunction={IconoirArrowRight} size="20" />
                </span>
              </button>
            )}
          {scrollToSection !== "" && (
            <button
              className="email-notification-action-button"
              onClick={() => {
                const elements =
                  document.getElementsByClassName(scrollToSection);
                elements[0] &&
                  elements[0].scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                    inline: "nearest",
                  });
              }}
              aria-label={actionRedirectLabel}
            >
              <span className="action-label">
                {actionRedirectLabel}
                <MotifIcon iconFunction={IconoirArrowRight} size="20" />
              </span>
            </button>
          )}
        </div>
        {!hideCloseBtn && (
          <button
            className="email-notification-close-btn"
            onClick={closeMessageHandler}
            aria-label="Close notification"
          >
            <MotifIcon iconFunction={IconoirXmark} size="16" />
          </button>
        )}
      </div>
    </div>
  );
}
